/**
 * The oracle — what answers `D §6.2`'s question, *have you actually lived
 * this?*
 *
 * `web.ts` carries seven node `qualifies`, three on Reach and four on Root,
 * and `epics.ts` three more on the Beast Epics; until this file existed every
 * one of them was refused by `NO_HISTORY` because nothing in the build could
 * answer a qualification at all. This is the answer — for the seven — and the
 * Beast three are refused here BY NAME with what the world is missing written
 * beside them. Two things about how it is written matter more than the rules
 * themselves.
 *
 * ## 1. Every qualification is read as CONSEQUENCE, never as attention
 *
 * Four of the seven are phrased in the language of watching — *followed*,
 * *witnessed*, *you were not standing at*. Laws 9 and 15 forbid any of that
 * being recorded: looking, hovering, naming, choosing, following and flying
 * write nothing, anywhere, and "attention does not create history, consequence
 * does" is the other half of the same law. So each one is answered by what the
 * god's hand actually did and what the world actually became:
 *
 * | qualification | what would satisfy it |
 * |---|---|
 * | kept-a-distant-care | acted on the SAME named subject at places far apart — the subject travelled and your hand was still on it |
 * | crossed-the-world | enduring deeds at both ends of the world |
 * | saw-a-system-whole | the settlement network durably changed, in several places, across years bracketed by your own history |
 * | a-place-with-a-century | one of your stones is a century old and mortals have been coming back to it |
 * | left-something-behind | durable change at your stone that was NOT your doing |
 * | three-bound-places | three built-up stones of yours, connected by ground mortals actually walk |
 * | the-deepest-ground | your single most built-up stone, at a rung generations of returning pay for |
 *
 * This is a strictly stronger reading than the literal one. A god cannot
 * qualify by staring at something; it qualifies by having changed it, or by
 * having been somewhere long enough that the world changed around it.
 *
 * ## 2. The Root half needed no journal, and that is a finding
 *
 * Three of Root's four read `AnchorSystem` — placement time, resonance, the
 * sacred rung mortals raised — which has existed since v0.3.2. They were never
 * unanswerable; they were unasked. Only Reach genuinely needed the durable
 * record, because only Reach asks about the god's own deeds across time and
 * distance, and that is precisely what the 2,000-record ring could not carry.
 *
 * ## 3. An unknown qualification is refused
 *
 * `answer()` dispatches on the qualification's stable id and returns false for
 * anything it does not recognise, so adding a `qualifies` to `web.ts` without
 * deciding here what would satisfy it fails closed — and `test/chronicle.ts`
 * asserts that every qualification in the Web has an answer here, which is the
 * same tripwire `bindings.ts` puts under every node's variable.
 *
 * Pure by law 7: no three, no `Math.random`, no clock. Every horizon is in
 * world-seconds, so nothing here moves when `D §4` multiplies the clock by 52.
 */

import type { AnchorState, SacredStage } from '@/sim/anchor';
import { SACRED_LADDER } from '@/sim/anchor';
import { Chronicle, SYSTEM_CHANGE } from '@/sim/chronicle';
import type { PathRecord } from '@/sim/mortals/types';
import type { HistoryOracle, Qualification } from './index';

/* ── the cuts, and where each number came from ─────────────────────────── */

/**
 * "Ground more than a region apart" (reach.crossed-the-world).
 *
 * The doc's word collides with the code's: `Region` IS the whole 49.9 km
 * world, so read literally the qualification can never fire. Read as its own
 * gloss — *both ends of the world* — and cut at half the world's box, which
 * `tools/chronicle.ts` measures at 25.0 km against a land diagonal of 53.7 km.
 * A fraction rather than a constant, so it stays honest on a world of any size.
 */
export const CROSSED_WORLD_FRACTION = 0.5;

/**
 * "A real separation" (reach.kept-a-distant-care), in metres.
 *
 * Measured rather than chosen: over three world-years a band drifts 537 m from
 * its own hearth and the trails it walks are p50 1,052 m, p90 1,389 m. So a
 * subject that has put a kilometre between itself and where you first touched
 * it has genuinely gone somewhere on this world's terms. Cut in kilometres —
 * the intuitive guess — this would never once fire.
 */
export const KEPT_CARE_TRAVEL = 1000;

/** "A century of its own history" (root.a-place-with-a-century), world-seconds. */
export const CENTURY = 100 * 365 * 86400;

/**
 * A system change is not one event (reach.saw-a-system-whole): it is the
 * network moving in several places over years. Three places and a decade —
 * the decade is what makes it a *change* rather than a busy afternoon, and the
 * three places are what make it a *system* rather than one camp.
 */
export const SYSTEM_PLACES = 3;
export const SYSTEM_SPAN = 10 * 365 * 86400;

/**
 * "Consequence at a stone you were not standing at" (root.left-something-
 * behind). Durable change within this far of the stone, that the god did not
 * cause, at least this many times. 400 m is the anchor's own neighbourhood —
 * wider than `NOTICE_RANGE`'s 130 m, narrower than the 1,040 m the marks are
 * folded onto, so a mark has to sit essentially on top of the stone.
 */
export const LEFT_RADIUS = 400;
export const LEFT_MARKS = 2;

/**
 * How bound a place has to be. The sacred ladder is the world's own measure of
 * depth and mortals climb it entirely by coming back: `cairn` is eighteen
 * visits and `shrine` is ninety, which `anchor.ts` calls the work of
 * generations.
 *
 * Resonance is deliberately NOT the bar. It starts at 1 the moment the stone
 * is placed and only ever decays, so any test of the form "resonance is high"
 * is passed most easily by a stone made this morning — the opposite of what
 * "deeply bound" means. What cannot be bought is the returning.
 */
export const BOUND_STAGE: SacredStage = 'cairn';
export const DEEPEST_STAGE: SacredStage = 'shrine';

/** How near a walked path must come to a stone to be said to reach it. */
export const PATH_NEAR = 300;

const rung = (s: SacredStage): number => SACRED_LADDER.indexOf(s);
const atLeast = (a: SacredStage, b: SacredStage): boolean => rung(a) >= rung(b);

/* ── what the oracle is allowed to see ─────────────────────────────────── */

/**
 * The world, as narrowly as a qualification needs it. An interface rather than
 * the `Simulation` so the oracle is testable against a hand-built world and
 * cannot quietly grow a dependency on the renderer or the clock.
 */
export interface WorldHistory {
  chronicle: Chronicle;
  anchors: readonly AnchorState[];
  paths: readonly PathRecord[];
  /** The world's box, in metres — `Region.extentMetres`. */
  extentMetres: number;
  /** World-seconds now. Passed in; nothing here reads a clock. */
  now: number;
}

/* ── the seven answers ─────────────────────────────────────────────────── */

const stonesOf = (w: WorldHistory, godId: string): AnchorState[] =>
  w.anchors.filter((a) => a.godId === godId);

/** Does this path come within PATH_NEAR of both stones? */
const links = (p: PathRecord, a: AnchorState, b: AnchorState): boolean => {
  let hitsA = false, hitsB = false;
  for (const pt of p.points) {
    if (!hitsA && Math.hypot(pt.x - a.x, pt.z - a.z) <= PATH_NEAR) hitsA = true;
    if (!hitsB && Math.hypot(pt.x - b.x, pt.z - b.z) <= PATH_NEAR) hitsB = true;
    if (hitsA && hitsB) return true;
  }
  return false;
};

export function answer(q: Qualification, godId: string, w: WorldHistory): boolean {
  const c = w.chronicle;
  switch (q.id) {
    /* ── Reach: the god's own deeds, across time and distance ─────────── */

    case 'reach.kept-a-distant-care': {
      // a strand whose subject is a NAMED thing — a cell cannot travel — and
      // whose acts have since landed a real distance from where it began
      return c.enduringOf(godId).some((s) =>
        !s.subject.startsWith('cell:') && s.travel >= KEPT_CARE_TRAVEL);
    }

    case 'reach.crossed-the-world':
      return c.spanOf(godId) >= w.extentMetres * CROSSED_WORLD_FRACTION;

    case 'reach.saw-a-system-whole': {
      // the settlement network durably different in several places, across a
      // decade, and bracketed by the god's own history rather than by its gaze
      const began = c.tallyOf(godId).first;
      if (began === undefined) return false;
      const marks = c.marks().filter((m) => SYSTEM_CHANGE.has(m.type) && m.first >= began);
      if (marks.length < SYSTEM_PLACES) return false;
      const places = new Set(marks.map((m) => m.key.slice(m.key.indexOf('|') + 1)));
      if (places.size < SYSTEM_PLACES) return false;
      let earliest = Infinity, latest = -Infinity;
      for (const m of marks) {
        earliest = Math.min(earliest, m.first);
        latest = Math.max(latest, m.last);
      }
      return latest - earliest >= SYSTEM_SPAN;
    }

    /* ── Root: what the places became ─────────────────────────────────── */

    case 'root.a-place-with-a-century':
      return stonesOf(w, godId).some((a) =>
        w.now - a.placedAt >= CENTURY && atLeast(a.stage, BOUND_STAGE));

    case 'root.left-something-behind': {
      // durable change at the stone that the god did not cause. This is the
      // whole of "you were not standing there" that laws 9 and 15 permit: not
      // where the presence was, but whose hand the change was not.
      return stonesOf(w, godId).some((a) => {
        const near = c.marksNear(a.x, a.z, LEFT_RADIUS, a.placedAt)
          .filter((m) => m.by !== godId && m.first >= a.placedAt);
        return near.reduce((n, m) => n + m.count, 0) >= LEFT_MARKS;
      });
    }

    case 'root.three-bound-places': {
      const bound = stonesOf(w, godId).filter((a) => atLeast(a.stage, BOUND_STAGE));
      if (bound.length < 3) return false;
      // Three of them connected by ground mortals actually walk — a connected
      // COMPONENT of size three, not merely three stones that happen to exist.
      //
      // Adjacency once, then one union pass. The obvious version restarts the
      // search from every stone and is O(n⁴·paths); a god with a few hundred
      // stones over an Age would make opening the Web hitch, and a panel that
      // stutters because of how much history you have is the wrong incentive.
      const n = bound.length;
      const parent = bound.map((_, i) => i);
      const find = (i: number): number => {
        while (parent[i] !== i) { parent[i] = parent[parent[i]]; i = parent[i]; }
        return i;
      };
      for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
          if (find(i) === find(j)) continue;
          if (w.paths.some((p) => links(p, bound[i], bound[j]))) parent[find(i)] = find(j);
        }
      }
      const size = new Map<number, number>();
      for (let i = 0; i < n; i++) {
        const r = find(i);
        const c = (size.get(r) ?? 0) + 1;
        if (c >= 3) return true;
        size.set(r, c);
      }
      return false;
    }

    case 'root.the-deepest-ground': {
      // "the single most bound ground of your existence" — the deepest stone
      // you have, and it must be deep in the way only returning makes a place
      const mine = stonesOf(w, godId);
      if (!mine.length) return false;
      let best = mine[0];
      for (const a of mine) if (rung(a.stage) > rung(best.stage)) best = a;
      return atLeast(best.stage, DEEPEST_STAGE);
    }

    /* ── Mortals: the one Become moment (`PLAYABLE.md` P5) ─────────────── */

    case 'mortals.a-life-became':
      // a strand of this god's that the world confirmed by a BECOMING — a
      // press that carried somebody over a line the card speaks at
      // (`chronicle.ts`, `person_changed` as the cast's causal child). Lived
      // once is lived: a becoming that has since lapsed with the life still
      // answers, because the question is whether it happened, not whether it
      // is still standing.
      return c.strandsOf(godId).some((s) => s.enduredAt !== undefined && s.left.includes('becoming'));

    /* ── Beasts: written, and refused until the world can remember a herd ── */

    /**
     * The three Beast Epics ask for lived history the durable record cannot
     * yet carry, and each one is refused HERE, by name, rather than falling
     * through — so `test/chronicle.ts`'s tripwire sees an answer written and
     * a reader sees exactly what is missing (WIRING.md carries the row).
     *
     *  · `beasts.moved-a-people-of-beasts` — "your hand on a herd's movement,
     *    and the world went on to call what followed a great migration". The
     *    hand exists now: `Simulation.act` presses a herd's own inclination to
     *    move and the cast is a strand on `herd:…`. What is missing is the
     *    ENDURANCE: `wildlife_migrated` is deliberately not durable (99.6% of
     *    the event stream), `wildlife_vanished` is durable but natural, and
     *    neither carries the pressing cast as a causal parent — and admitting a
     *    beast endurance is an eleventh kind of consequence, which re-derives
     *    `STANDING_AT_100` (`level.ts`; `test/level.ts` §9 pins the ten). That
     *    is a ruling about the level economy, not a wire.
     *  · `beasts.kept-a-line` — no animal has kin (`bindings.ts`,
     *    `beast.subject.lineage`), so no descendant can confirm anything.
     *  · `beasts.taught-the-wild` — wildlife never hears an omen and no animal
     *    learns, so no behaviour can be changed by a sign, let alone outlast it.
     */
    case 'beasts.moved-a-people-of-beasts':
    case 'beasts.kept-a-line':
    case 'beasts.taught-the-wild':
      return false;

    /* ── and anything the design adds without deciding this ───────────── */
    default:
      return false;
  }
}

/**
 * The oracle for one deity, bound to a world. This is what replaces
 * `NO_HISTORY` on a `GodhoodWeb` the moment there is a world to ask.
 *
 * `w` is captured by reference and `now` is read off it at call time, so a
 * panel that opens twice sees the world as it is twice — the Web asks the
 * question, the world answers it, and neither caches the other.
 */
export const chronicleOracle = (godId: string, w: WorldHistory): HistoryOracle =>
  (q: Qualification) => answer(q, godId, w);

/** As much of a `Simulation` as a qualification is allowed to see. */
export interface HistorySource {
  chronicle: Chronicle;
  anchors: { all(): readonly AnchorState[] };
  bands: { paths(): readonly PathRecord[] };
  region: { extentMetres: number };
}

/**
 * A `WorldHistory` view onto a live simulation. Getters throughout, and the
 * simulation itself behind a thunk, because BOTH of the things this reads move
 * underneath it: a `GodhoodWeb` is built once at boot and asked about the world
 * several centuries later, and `main.ts` REPLACES its `sim` outright when a
 * world is adopted or loaded. A view that captured either by value would
 * silently answer questions about a world that no longer exists — which is the
 * quiet-loss failure mode `SNAPSHOT_VERSION` exists to make loud.
 *
 * Structurally typed rather than importing `Simulation`, so `test/` can pass a
 * hand-built world and nothing under `godhood/` acquires a dependency on the
 * whole simulation.
 */
export const worldHistory = (
  sim: () => HistorySource,
  now: () => number,
): WorldHistory => ({
  get chronicle() { return sim().chronicle; },
  get anchors() { return sim().anchors.all(); },
  get paths() { return sim().bands.paths(); },
  get extentMetres() { return sim().region.extentMetres; },
  get now() { return now(); },
});
